/**
 * Role helpers.
 *
 * Roles are written to localStorage at login (src/App.tsx for the MSAL path in
 * qa/prod, src/pages/login.tsx for the dev login page) from the /auth/login
 * response: `roles` is a list of RoleIDs, `roleNames` a list of RolesMaster names.
 *
 * The Contractor exemption is matched on the role NAME rather than the ID,
 * because that is what the SQL side matches on (SubmitTimesheetForApproval and
 * AddOrUpdateTimesheetLine_v1 both test `RolesMaster.RoleName = 'Contractor'`).
 * Keying both sides on the same value keeps them from drifting apart - but it
 * also means renaming the role in RolesMaster requires updating both this
 * constant and the SQL that references it.
 */

export const CONTRACTOR_ROLE = "Contractor"

/** Role names for the signed-in user, lowercased and trimmed. Empty if none are stored. */
function getRoleNames(): string[] {
  try {
    const raw = localStorage.getItem("roleNames")
    if (!raw) return []
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((r): r is string => typeof r === "string")
      .map((r) => r.trim().toLowerCase())
  } catch {
    // Malformed or unavailable storage - treat as "no roles" so the caller falls
    // back to the stricter path rather than accidentally granting an exemption.
    return []
  }
}

/**
 * True when the signed-in user holds the Contractor role.
 *
 * Contractors are exempt from the timesheet hour rules (minimum 8h/day, minimum
 * 40h/week, maximum daily hours). They are NOT exempt from the per-cell rules -
 * submitted/approved lock, future dates, engagement date range - nor from the
 * structural checks that keep a timesheet well-formed.
 *
 * This is a UI convenience only. The database enforces the same exemption, and that
 * is the check that actually decides; this one just stops the UI from blocking a
 * request the server would have accepted.
 */
export function isContractor(): boolean {
  return getRoleNames().includes(CONTRACTOR_ROLE.toLowerCase())
}
